import { useState, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import type { ProjectDetail } from '../../services/projectService';
import type { SprintListItem } from '../../services/sprintService';
import { useCreateSprint } from '../../hooks/useSprints';
import SprintColumn from './SprintColumn';
import SprintCreateModal from './SprintCreateModal';

interface Props {
  project: ProjectDetail;
  sprints: SprintListItem[];
  isLoading: boolean;
  onDropIssue: (sprintId: string, issueId: string) => void;
}

export default function SprintPanel({
  project,
  sprints,
  isLoading,
  onDropIssue,
}: Props) {
  const createMutation = useCreateSprint(project.id);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // COMPLETED 스프린트 제외
  const visibleSprints = useMemo(
    () => sprints.filter((s) => s.status !== 'COMPLETED'),
    [sprints],
  );

  const handleCreateSprint = useCallback(() => {
    if (sprints.length === 0) {
      // 첫 스프린트: 모달로 이름/기간 설정
      setShowCreateModal(true);
    } else {
      // 이후: 바로 생성 (이름 자동 채번)
      createMutation.mutate();
    }
  }, [sprints.length, createMutation]);

  const handleModalSubmit = useCallback(
    (data: { name?: string; startDate?: string; endDate?: string; duration?: number }) => {
      createMutation.mutate(data, {
        onSuccess: () => setShowCreateModal(false),
      });
    },
    [createMutation],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        스프린트 로딩 중...
      </div>
    );
  }

  return (
    <div className="flex h-full gap-3 overflow-x-auto">
      {visibleSprints.map((sprint) => (
        <SprintColumn
          key={sprint.sprintId}
          sprint={sprint}
          projectId={project.id}
          allSprints={sprints}
          onDropIssue={onDropIssue}
        />
      ))}

      {/* Create Sprint Button */}
      <button
        onClick={handleCreateSprint}
        disabled={createMutation.isPending}
        className="flex items-center justify-center gap-1.5 w-[300px] shrink-0 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
      >
        <Plus className="w-4 h-4" />
        {createMutation.isPending ? '생성 중...' : '스프린트 만들기'}
      </button>

      {/* Create Modal (first sprint only) */}
      <SprintCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleModalSubmit}
        isSubmitting={createMutation.isPending}
      />
    </div>
  );
}
